import { Loader } from '@any-ui-react/core';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { z } from 'zod';

import { ErrorCard } from '@/common/components/shared/error-card';
import { useAuthContext } from '@/common/contexts';
import { SignInCallbackErrorCode, useSignInCallbackQuery } from '@/common/graphql';
import {
  AppEnvironment,
  AUTH_PKCE_VERIFIER_KEY,
  AUTH_STATE_KEY,
  forgetSignInAccount,
  getSessionStorageItem,
  getStorageItem,
  isSharedComputer,
  rememberSignInAccount,
  removeSessionStorageItem,
  setAuthUserInSession,
} from '@/common/utils';

import { getSignInErrorContent } from './signInError';

// RFC 7636 code verifier, as stored by the sign-in page of this tab.
const CodeVerifierSchema = z.string().regex(/^[A-Za-z0-9._~-]{43,128}$/);

export const SignInCallback = () => {
  const { setAuthUser } = useAuthContext();
  const navigate = useNavigate();
  const { search } = useLocation();
  const isProcessing = useRef(false);

  const authState = useMemo(() => getStorageItem(AUTH_STATE_KEY) ?? '', []);
  const { code, isValidState } = useMemo(() => {
    const query = new URLSearchParams(search);
    return {
      code: query.get('code') ?? '',
      isValidState: authState === query.get('state'),
    };
  }, [authState, search]);
  // Absent when the sign-in was started elsewhere; the server then decides whether that is still accepted.
  const codeVerifier = useMemo(() => {
    const parsed = CodeVerifierSchema.safeParse(getSessionStorageItem(AUTH_PKCE_VERIFIER_KEY));
    return parsed.success ? parsed.data : null;
  }, []);
  const [isError, setError] = useState(false);
  const [errorCode, setErrorCode] = useState<SignInCallbackErrorCode>();

  const isSkipped = !authState || !code || !isValidState;
  const { data, loading } = useSignInCallbackQuery({
    variables: {
      code,
      redirectUri:
        import.meta.env.VITE_ENVIRONMENT === AppEnvironment.LOCAL ? import.meta.env.VITE_AUTH_CALLBACK_URL : undefined,
      codeVerifier,
    },
    skip: isSkipped,
    errorPolicy: 'all',
  });

  // The verifier is single-use: discard it once the request is settled or skipped, whatever the outcome
  // (it was read above before the request was sent).
  useEffect(() => {
    if (isSkipped || !loading) {
      removeSessionStorageItem(AUTH_PKCE_VERIFIER_KEY);
    }
  }, [isSkipped, loading]);

  useEffect(() => {
    if (isProcessing.current || loading || !data?.signInCallback) {
      return;
    }

    isProcessing.current = true;

    if (data.signInCallback.__typename === 'SignInCallbackSuccess') {
      setError(false);

      const authUser = setAuthUserInSession(data.signInCallback.accessToken ?? '');
      setAuthUser(authUser);
      // On a shared computer nobody's account is kept, including one remembered before the box was checked.
      if (isSharedComputer()) {
        forgetSignInAccount();
      } else {
        rememberSignInAccount(authUser.email);
      }

      let redirect = getStorageItem('redirect');
      if (redirect === '/login') {
        redirect = '/dashboard';
      }

      navigate(redirect || '/', { replace: true });
    } else if (data?.signInCallback.__typename === 'SignInCallbackError') {
      setErrorCode(data.signInCallback.errorCode);
      // Stop suggesting an account MED does not accept; other failures keep the remembered account.
      if (data.signInCallback.errorCode === SignInCallbackErrorCode.UserNotFound) {
        forgetSignInAccount();
      }
      setError(true);
      isProcessing.current = false;
    }
  }, [data?.signInCallback, loading, navigate, setAuthUser]);

  const errorContent = getSignInErrorContent(errorCode);

  return loading ? (
    <Loader />
  ) : !isError ? (
    <></>
  ) : (
    <ErrorCard
      buttonLink="/login"
      buttonText="Sign in Again"
      guideline={errorContent.guideline}
      title={errorContent.title}
    />
  );
};
